<template>
  <span>
    <el-dialog
      :visible.sync="dialogVisible"
      :destroy-on-close="true"
      :close-on-click-modal="!submitting"
      :close-on-press-escape="!submitting"
      :show-close="!submitting"
      width="75%"
      top="5vh"
    >
      <div slot="title">
        <h3 class="title">{{ $t("edit_backlink") }}</h3>
      </div>
      <el-alert
        class="price-update-alert"
        type="info"
        :title="$t('price_update_info')"
        :closable="false"
        show-icon
      />
      <dynamic-form
        ref="dynamicForm"
        v-model="updatedBacklink"
        :form="data.form"
        :default-search-engine="defaultSearchEngine"
        @isDomainSourceCorrectlyFormatted="
          handleDomainSourceIncorrectlyFormatted($event)
        "
        @isTargetUrlCorrectlyFormattedOrEmpty="
          handleTargetUrlIncorrectlyFormatted($event)
        "
        @pending-supplier-change="hasPendingSupplier = $event"
        @invalid-supplier-change="hasInvalidSupplier = $event"
      >
      </dynamic-form>
      <div class="submit">
        <el-button
          :disabled="
            !isTargetUrlCorrectlyFormattedOrEmpty ||
            !isDomainSourceCorrectlyFormatted ||
            hasInvalidSupplier ||
            submitting
          "
          :loading="submitting"
          type="success"
          plain
          @click="submit()"
        >
          {{ $t("validate") }}
        </el-button>
        <div
          v-if="hasPendingSupplier"
          class="pending-supplier-warning">
          {{ $t("pending_supplier_warning") }}
        </div>
        <div
          v-if="hasInvalidSupplier"
          class="invalid-supplier-warning">
          {{ $t("invalid_supplier_warning") }}
        </div>
        <div
          v-if="submitError"
          class="submit-error">
          {{ $t("submit_error") }}
        </div>
      </div>
    </el-dialog>
  </span>
</template>

<script>
import DynamicForm from "@/components/DynamicForm";

export default {
  props: {
    value: Boolean,
    data: Object,
    defaultSearchEngine: Object,
  },
  components: {
    DynamicForm,
  },
  data() {
    return {
      updatedBacklink: {},
      isTargetUrlCorrectlyFormattedOrEmpty: false,
      isDomainSourceCorrectlyFormatted: false,
      submitting: false,
      submitError: false,
      hasPendingSupplier: false,
      hasInvalidSupplier: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(dialogVisible) {
        this.$emit("input", dialogVisible);
      },
    },
  },
  watch: {
    value(visible) {
      if (visible) {
        this.hasPendingSupplier = false;
        this.hasInvalidSupplier = false;
        this.submitError = false;
      }
    },
  },
  methods: {
    async submit() {
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      this.submitError = false;
      try {
        await this.$refs.dynamicForm.beforeSubmit();
      } catch (err) {
        this.submitting = false;
        this.submitError = true;
        return;
      }
      let details = {};
      details.entity = this.updatedBacklink;
      details.link = this.data;
      this.$emit("update-backlink", details);
      this.submitting = false;
      this.dialogVisible = false;
    },
    handleDomainSourceIncorrectlyFormatted(isDomainSourceCorrectlyFormatted) {
      this.isDomainSourceCorrectlyFormatted = isDomainSourceCorrectlyFormatted;
    },
    handleTargetUrlIncorrectlyFormatted(isTargetUrlCorrectlyFormattedOrEmpty) {
      this.isTargetUrlCorrectlyFormattedOrEmpty =
        isTargetUrlCorrectlyFormattedOrEmpty;
    },
  },
};
</script>

<style lang="scss" scoped>
.price-update-alert.el-alert--info {
  margin-bottom: 16px;
  background-color: #ecf5ff;
  color: #409eff;

  ::v-deep .el-alert__title {
    color: #409eff;
  }
}

.form {
  overflow-y: scroll;
  max-height: 700px;

  .fat-divider {
    height: 3px;
  }

  .collapse-form {
    margin-bottom: 2rem;
    margin-right: 1.5rem;
    margin-left: 1.5rem;
    margin-top: 1rem;
  }
}

.title {
  text-align: center;
}

.submit {
  margin-top: 1rem;
  text-align: center;
}

.submit-error {
  margin-top: 0.5rem;
  color: #f56c6c;
  font-size: 12px;
}

.pending-supplier-warning {
  margin-top: 0.5rem;
  color: #e6a23c;
  font-size: 12px;
}

.invalid-supplier-warning {
  margin-top: 0.5rem;
  color: #f56c6c;
  font-size: 12px;
}
</style>

<i18n>
{
  "en": {
    "edit_backlink": "Edit Backlink",
	"validate": "Validate",
	"submit_error": "Something went wrong, please try again.",
	"pending_supplier_warning": "A new intermediary supplier will be created when you validate.",
	"invalid_supplier_warning": "This supplier no longer exists, please select another one before validating.",
	"price_update_info": "The buying price entered will replace this supplier's offer price for this domain, whatever the backlink's status."
  },
  "fr": {
    "edit_backlink": "Backlink",
	"validate": "Valider",
	"submit_error": "Une erreur est survenue, veuillez réessayer.",
	"pending_supplier_warning": "Un nouveau fournisseur intermédiaire va être créé à la validation.",
	"invalid_supplier_warning": "Ce fournisseur n'existe plus, veuillez en sélectionner un autre avant de valider.",
	"price_update_info": "Le prix d'achat saisi remplacera le prix de l'offre de ce fournisseur pour ce domaine, quel que soit le statut du backlink."
  },
  "de": {
    "edit_backlink": "Rückverlinkung",
	"validate": "Bestätigen",
	"submit_error": "Etwas ist schiefgelaufen, bitte versuchen Sie es erneut.",
	"pending_supplier_warning": "Ein neuer Vermittler-Anbieter wird bei der Bestätigung erstellt.",
	"invalid_supplier_warning": "Dieser Anbieter existiert nicht mehr, bitte wählen Sie einen anderen vor der Bestätigung aus.",
	"price_update_info": "Der eingegebene Einkaufspreis ersetzt den Angebotspreis dieses Anbieters für diese Domain, unabhängig vom Status des Backlinks."
  }
}
</i18n>
